import { Component, Inject, OnInit, DOCUMENT } from '@angular/core';
import { IconSize } from '@predictiveindex/tapestry';
import { distinctUntilChanged } from 'rxjs';
import { CurrentModuleOptions } from '../../../constants-types';
import { ModuleName } from '../../../core-data-model';
import { SiteNavigationService } from '../../../core-services';
import { DateFormatUtility } from '../../../core-utilities';
import {
    DemoDataService,
    FeatureFlags,
    FeatureFlagsService,
    ModuleAccessService
} from '../../../system-services';
import { TranslationService } from '../../../translations';
import { UpgradeAccountService } from '../../upgrade-account';
import { getV2ModuleData } from './utils/module-utils';

@Component({
    selector: 'time-trial-banner',
    templateUrl: './time-trial.component.html',
    styleUrls: ['./time-trial.component.less'],
    standalone: false
})
export class TimeTrialComponent implements OnInit {
    iconSize = IconSize.Medium;
    /* The following nav heights are created so that we can dynamically adjust the height of the Navbar.
      In the case of a Banner we add the default Nav height + banner height aka `navHeightHigh` and
      we apply that dynamically by accessing the documentElement property.
    */
    navHeightStd = '68px';
    navHeightHigh = '119px';
    currentClientModule: string = CurrentModuleOptions.Platform;
    messageContent: string;
    upgradeMessageKey: string;
    showBanner = false;

    constructor(
        private featureFlagService: FeatureFlagsService,
        private siteNavigation: SiteNavigationService,
        private moduleAccess: ModuleAccessService,
        private TR: TranslationService,
        private dateUtil: DateFormatUtility,
        private upgradeAccountService: UpgradeAccountService,
        @Inject(DOCUMENT) private document: Document,
        private demoDataService: DemoDataService
    ) {}

    ngOnInit() {
        this.siteNavigation.currentClientModule$
            .pipe(distinctUntilChanged())
            .subscribe((currentModule: string) => {
                this.currentClientModule = currentModule;
                const { moduleName, v2ModuleStringName } =
                    getV2ModuleData(currentModule);

                this.showBanner =
                    !this.moduleAccess.isPaidAccount(moduleName) &&
                    this.moduleAccess.isTimeTrialEnabled(moduleName) &&
                    this.showBannerForCurrentModule(v2ModuleStringName) &&
                    !this.siteNavigation.isInOnboarding() &&
                    !this.siteNavigation.isInExpired();

                this.loadBanner(currentModule);
            });
    }

    loadBanner(clientModule: string) {
        const { moduleName } = getV2ModuleData(clientModule);
        const isDemoBannerEnabled = this.demoDataService.isDemoEnabled();

        if (this.showBanner) {
            // Adjust the Global Nav Height with Banner height
            this.document.documentElement.style.setProperty(
                '--globalNavHeight',
                `${this.navHeightHigh}`
            );
            // Fetch Trial Info
            const trialInfo = this.moduleAccess.getTimeTrialDetails(moduleName);

            // Build Banner content
            this.messageContent = this.constructTranslationKeyName(
                new Date(trialInfo.endDate),
                moduleName
            );
            this.upgradeMessageKey = `timeTrial.${moduleName.toLowerCase()}.upgradeMessage`;
            return;
        }

        if (!isDemoBannerEnabled) {
            // Only safe to reset if demoData is not turned on
            // DemoData and TimeTrial are mutually exclusive
            this.resetBannerHeight();
        }
    }

    constructTranslationKeyName(endDate: Date, moduleName: ModuleName) {
        const currentDate = new Date(Date.now());

        if (currentDate.getTime() < endDate.getTime()) {
            const daysRemaining = this.dateUtil.getDateDiff(endDate);

            if (daysRemaining > 1) {
                return this.TR.keyFormatter(
                    `timeTrial.${moduleName.toLowerCase()}.moreThanADay`,
                    daysRemaining
                );
            } else if (daysRemaining === 1) {
                return this.TR.key(
                    `timeTrial.${moduleName.toLowerCase()}.oneDayRemaining`
                );
            } else if (daysRemaining === 0) {
                return this.TR.key(
                    `timeTrial.${moduleName.toLowerCase()}.today`
                );
            }
        } else {
            return this.TR.key(`timeTrial.${moduleName.toLowerCase()}.expired`);
        }
    }

    async upgradeAccount() {
        await this.upgradeAccountService.upgradeAccount(false);
    }

    showBannerForCurrentModule(moduleName: string) {
        // Check if ModuleSpecific FF is enabled
        return this.featureFlagService.getByFlag(
            FeatureFlags[`enable${moduleName}TimeTrialBanner`]
        );
    }

    private resetBannerHeight() {
        // Adjust the Global Nav Height without Banner
        this.document.documentElement.style.setProperty(
            '--globalNavHeight',
            `${this.navHeightStd}`
        );
    }
}
